/**
 * Which checkpoint holds a scan before any design. Two answers, on purpose:
 *
 *   - `pickAsFoundCheckpoint` — what the inspection INSPECTS
 *     (claude-docs/2026-10-15-inspection-report-items-design.md): the
 *     processing's own reading of the scan, never the inspection's output.
 *   - `pickAsScannedCheckpoint` — what the checkpoint list calls "As scanned"
 *     and the estimate prices demolition from: the newest processing
 *     checkpoint, the inspection's when one has run.
 *
 * Plus the processing stages and the list's visibility rule. Pure.
 */

/**
 * The name `createProcessedCheckpoint` gives the checkpoint it writes after
 * post-processing (functions/features/checkpoint-utils.js). Every processing
 * writes a new one, so the newest is the current pipeline's reading of the scan.
 */
const AS_FOUND_CHECKPOINT_NAME = 'Post-Processing Complete';

/**
 * The newest "Post-Processing Complete" checkpoint, else the newest "Initial
 * Import" (a scan processed before the processed checkpoint existed: RoomPlan's
 * own objects, no rebuilt cabinets or countertops), else null.
 * `checkpoints`: checkpoint docs, each with `name`, `isInitial`, `createdAt`.
 */
function pickAsFoundCheckpoint(checkpoints) {
  const newestFirst = newestFirstOf(checkpoints);
  return (
    newestFirst.find((c) => c.name === AS_FOUND_CHECKPOINT_NAME) ||
    newestFirst.find((c) => c.isInitial) ||
    null
  );
}

/**
 * The checkpoints the processing writes, by STAGE, in the order a scan passes
 * through them: the scan as imported (before post-processing), the finished
 * structure after post-processing, and the finished structure with the
 * inspection's devices added. Each carries `source: 'processing'` and its
 * `stage`; a checkpoint written before those fields existed is recognised by
 * its name.
 */
const PROCESSING_STAGES = {
  IMPORT: 'import',
  PROCESSED: 'processed',
  INSPECTION: 'inspection',
};

/** The name `createInspectionCheckpoint` gives the inspection's checkpoint. */
const INSPECTION_CHECKPOINT_NAME = 'Inspection Complete';

/** What the newest processing checkpoint is called in the list. */
const AS_SCANNED_LABEL = 'As scanned';

const LEGACY_STAGE_BY_NAME = {
  'Initial Import': PROCESSING_STAGES.IMPORT,
  [AS_FOUND_CHECKPOINT_NAME]: PROCESSING_STAGES.PROCESSED,
};

/**
 * The processing stage a checkpoint records, or null for one a person (or a
 * proposal) saved. A legacy "Initial Import" counts only with `isInitial`, the
 * mark the processing always gave it.
 */
function processingStageOf(checkpoint) {
  if (!checkpoint) return null;
  if (checkpoint.source === 'processing') return checkpoint.stage || null;
  if (checkpoint.source) return null;
  const legacy = LEGACY_STAGE_BY_NAME[checkpoint.name];
  if (legacy === PROCESSING_STAGES.IMPORT && !checkpoint.isInitial) return null;
  return legacy || null;
}

/**
 * When a checkpoint was written, in ms. The server writes ISO strings; a web
 * save writes a Firestore timestamp (and `clientCreatedAt` until it lands),
 * so one list mixes both and a string compare would order them by type.
 */
function createdMillis(checkpoint) {
  const at = checkpoint && (checkpoint.createdAt || checkpoint.clientCreatedAt);
  if (!at) return 0;
  if (typeof at.toMillis === 'function') return at.toMillis();
  if (typeof at.toDate === 'function') return at.toDate().getTime();
  if (Number.isFinite(at.seconds)) return at.seconds * 1000;
  const ms = Date.parse(at);
  return Number.isFinite(ms) ? ms : 0;
}

function newestFirstOf(checkpoints) {
  return [...(checkpoints || [])].sort(
    (a, b) => createdMillis(b) - createdMillis(a)
  );
}

/**
 * The checkpoint the list calls "As scanned": the newest one the processing
 * wrote, whatever its stage — after an inspection that is the inspection's,
 * otherwise the newest post-processing one. Null when there is none.
 *
 * What the estimate prices demolition from. Not `pickAsFoundCheckpoint`: that
 * one is what the inspection INSPECTS, so it must never be a checkpoint the
 * inspection itself wrote.
 */
function pickAsScannedCheckpoint(checkpoints) {
  return (
    newestFirstOf(checkpoints).find((c) => processingStageOf(c) !== null) ||
    null
  );
}

/**
 * The checkpoints a viewer of the list sees, newest first, each with
 * `displayName`. Every processing checkpoint is admin-only except the newest,
 * which everyone sees as "As scanned" (the user, 2026-10-16); an admin sees
 * the rest under their own names. Checkpoints a person or a proposal saved
 * are unaffected.
 *
 * @param {Array<object>} checkpoints checkpoint docs (`name`, `createdAt`, …)
 * @param {{ isAdmin: boolean }} viewer
 * @returns {Array<object & { displayName: string, asScanned: boolean,
 *   processingStage: string|null }>}
 */
function visibleCheckpoints(checkpoints, { isAdmin }) {
  const asScanned = pickAsScannedCheckpoint(checkpoints);
  return newestFirstOf(checkpoints)
    .map((c) => {
      const stage = processingStageOf(c);
      const isAsScanned = asScanned !== null && c === asScanned;
      return {
        ...c,
        processingStage: stage,
        asScanned: isAsScanned,
        displayName: isAsScanned ? AS_SCANNED_LABEL : nameOf(c),
      };
    })
    .filter((c) => isAdmin || c.processingStage === null || c.asScanned);
}

/** A checkpoint's own name; older web saves stored `{ name }` objects. */
function nameOf(checkpoint) {
  const { name } = checkpoint;
  if (name && typeof name === 'object')
    return name.name || 'Unnamed Checkpoint';
  return name || 'Unnamed Checkpoint';
}

/**
 * The run whose results a scan's docs hold: the `runId` of the most recently
 * measured doc. A re-run replaces every doc it answers, so this is the run the
 * report is about. Null when no doc names one.
 */
function reportRunId(docs) {
  let best = null;
  for (const d of docs || []) {
    if (!d || !d.runId) continue;
    if (!best || String(d.measuredAt) > String(best.measuredAt)) best = d;
  }
  return best ? best.runId : null;
}

module.exports = {
  AS_FOUND_CHECKPOINT_NAME,
  INSPECTION_CHECKPOINT_NAME,
  AS_SCANNED_LABEL,
  PROCESSING_STAGES,
  processingStageOf,
  pickAsFoundCheckpoint,
  pickAsScannedCheckpoint,
  visibleCheckpoints,
  reportRunId,
};
